import React, { useState } from "react";
import { Box, Button, Card, CardContent, Divider, IconButton, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { setDiscount, clearDiscount } from "../../../Store/reducers/cartReducer";
import { computeTotals, formatPLN } from "../../../config/checkout";
import FreeShippingProgress from "../../../components/FreeShipping/FreeShippingProgress";
import "./OrderSummaryPanel.scss";

const API_URL = process.env.REACT_APP_API_URL;

// Totals, discount code and the step button. The product list lives only in
// BasketStep. On the shipping step the button submits the shipping form
// (`shippingFormId`) so its validation always runs.
const OrderSummaryPanel = ({ activeStep = 0, onNext, shippingMethod, shippingFormId, disableCheckout, blocked = false }) => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const products = useSelector((state) => state.cart.products);
    const discount = useSelector((state) => state.cart.discount);
    const [code, setCode] = useState("");
    const [codeError, setCodeError] = useState("");
    const [applying, setApplying] = useState(false);

    const totals = computeTotals(products, discount, activeStep >= 2 ? shippingMethod : null);

    const applyCode = async (e) => {
        e.preventDefault();
        const wanted = code.trim();
        if (!wanted) return;
        setApplying(true);
        setCodeError("");
        try {
            const res = await fetch(`${API_URL}/discount-codes/validate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ code: wanted, subtotal: totals.subtotal }),
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok) {
                const details = json?.error?.details || {};
                setCodeError(
                    details.reason === "min_order"
                        ? intl.formatMessage({ id: "discount_min_order" }, { amount: formatPLN(details.minOrderAmount) })
                        : intl.formatMessage({ id: "discount_invalid" })
                );
                return;
            }
            const { code: applied, discountType, value } = json.data;
            dispatch(setDiscount({ code: applied, discountType, value }));
            setCode("");
        } catch {
            setCodeError(intl.formatMessage({ id: "network_error" }));
        } finally {
            setApplying(false);
        }
    };

    const step = disableCheckout ? null : activeStep;
    const button =
        step === 0
            ? { text: "checkout_go_to_shipping", props: { onClick: onNext, disabled: products.length === 0 || blocked } }
            : step === 1
                ? { text: "checkout_go_to_payment", props: { type: "submit", form: shippingFormId } }
                : null;

    return (
        <Card className="order-summary-panel">
            <CardContent sx={{ p: { xs: 2, sm: 3 }, "&:last-child": { pb: { xs: 2, sm: 3 } } }}>
                <Typography variant="h6" gutterBottom>
                    <FormattedMessage id="order_summary" />
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <div className="summary-row">
                    <span><FormattedMessage id="summary_products" /></span>
                    <span>{formatPLN(totals.subtotal)}</span>
                </div>

                {discount && (
                    <div className="summary-row summary-discount">
                        <span>
                            <FormattedMessage id="summary_discount" values={{ code: discount.code }} />
                            <IconButton
                                size="small"
                                onClick={() => dispatch(clearDiscount())}
                                aria-label={intl.formatMessage({ id: "discount_remove" })}
                                sx={{ ml: 0.5, p: 0.25 }}
                            >
                                <CloseIcon sx={{ fontSize: "0.9rem" }} />
                            </IconButton>
                        </span>
                        <span>−{formatPLN(totals.discountAmount)}</span>
                    </div>
                )}

                <div className="summary-row">
                    <span><FormattedMessage id="summary_shipping" /></span>
                    <span>
                        {totals.shipping === null ? (
                            totals.freeShipping
                                ? <FormattedMessage id="shipping_free" />
                                : <FormattedMessage id="shipping_next" />
                        ) : totals.shipping === 0 ? (
                            <FormattedMessage id="shipping_free" />
                        ) : (
                            formatPLN(totals.shipping)
                        )}
                    </span>
                </div>

                <FreeShippingProgress amount={totals.discounted} sx={{ my: 2 }} />

                {!discount && !disableCheckout && (
                    <Box component="form" onSubmit={applyCode} sx={{ display: "flex", gap: 1, mb: 2 }}>
                        <TextField
                            size="small"
                            fullWidth
                            value={code}
                            onChange={(e) => {
                                setCode(e.target.value);
                                if (codeError) setCodeError("");
                            }}
                            placeholder={intl.formatMessage({ id: "discount_placeholder" })}
                            error={!!codeError}
                            helperText={codeError || " "}
                            inputProps={{ maxLength: 64, "aria-label": intl.formatMessage({ id: "discount_placeholder" }) }}
                        />
                        <Button
                            type="submit"
                            variant="outlined"
                            disabled={applying || !code.trim()}
                            sx={{ flexShrink: 0, height: 40, color: "text.primary", borderColor: "text.primary" }}
                        >
                            <FormattedMessage id="discount_apply" />
                        </Button>
                    </Box>
                )}

                <Divider />
                <div className="summary-row summary-total">
                    <span><FormattedMessage id="summary_total" /></span>
                    <span>{formatPLN(totals.total)}</span>
                </div>

                {button && (
                    <Button
                        {...button.props}
                        fullWidth
                        variant="contained"
                        sx={{
                            mt: 2,
                            py: 1.5,
                            bgcolor: "text.primary",
                            color: "background.default",
                            "&:hover": { bgcolor: "text.secondary" },
                        }}
                    >
                        <FormattedMessage id={button.text} />
                    </Button>
                )}
            </CardContent>
        </Card>
    );
};

export default OrderSummaryPanel;
